import { authQueryOptions } from "@/features/auth/api/useAuthQuery";
import { verificationCompleteRouteSearchSchema } from "@/features/auth/search/authRouteSearch";
import { needsPasswordSetup } from "@/features/auth/utils/inviteAccountSetup";
import { selectProfileByIdQueryOptions } from "@/features/profile/api/selectProfile";
import { supabaseClient } from "@/lib/supabase";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { SmartphoneCheck } from "@/features/auth/components/SmartphoneCheckIcon";
import { MailCheck, X } from "lucide-react";
import { useEffect, useState } from "react";
import { validate as validateUUID } from "uuid";
import { VerificationCompleteScreen } from "@/features/auth/screens/VerificationCompleteScreen";
import { usersOrganisationsRolesQueryOptions, useSetRoles } from "@/features/auth/api/selectUsersOrganisationsRoles";
import { useAuthCopy } from "@/features/i18n/authCopy";
import { useAuth } from "@/utils/auth";

const VerificationComplete = () => {
    const copy = useAuthCopy();
    const auth = useAuth();
    const { via } = Route.useSearch();
    const isSms = via === "sms";
    const { retry } = useSetRoles();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            retry();
            setIsLoading(false);
        }, 2000);

        return () => clearTimeout(timer);
    }, [retry]);

    const verifyingTitle = isSms ? copy.verifyingPhone : copy.verifyingEmail;
    const successTitle = isSms ? copy.phoneVerified : copy.emailVerified;
    const verifyLoadingDescription = isSms ? copy.verifyingPhoneWait : copy.verifyingEmailWait;
    const successDescription = isSms ? copy.phoneVerifiedSuccess : copy.emailVerifiedSuccess;
    const errorDescription = isSms ? copy.verifyPhoneError : copy.verifyEmailError;

    return (
        <VerificationCompleteScreen
            title={isLoading ? verifyingTitle : auth.roles?.hasRole ? successTitle : copy.verificationError}
            description={
                isLoading ? verifyLoadingDescription : auth.roles?.hasRole ? successDescription : errorDescription
            }
            icon={isLoading ? undefined : auth.roles?.hasRole ? (isSms ? SmartphoneCheck : MailCheck) : X}
            isLoading={isLoading}
            verifyLoadingDescription={verifyLoadingDescription}
            primaryTo={auth.roles?.isClinician ? "/onboarding/clinician/name" : "/onboarding/name"}
            showTryAgain={!auth.roles?.hasRole}
        />
    );
};

export const Route = createFileRoute("/_auth/verification-complete")({
    validateSearch: (search) => verificationCompleteRouteSearchSchema.parse(search),
    component: VerificationComplete,
    beforeLoad: async ({ context }) => {
        await context?.queryClient.ensureQueryData(authQueryOptions());

        const {
            data: { user },
        } = await supabaseClient.auth.getUser();

        if (user?.id) {
            const profile = await context?.queryClient.ensureQueryData(selectProfileByIdQueryOptions(user.id));
            if (profile && needsPasswordSetup(profile)) {
                throw redirect({ to: "/change-password", search: { source: "confirm-account" } });
            }
        }

        return context;
    },
    loader: async ({ context }) => {
        const qc = context?.queryClient;
        const userId = context?.auth?.id?.trim();
        if (!qc || !userId || !validateUUID(userId)) {
            return;
        }

        try {
            const roles = await qc.ensureQueryData(usersOrganisationsRolesQueryOptions(userId));
            context.auth.roles?.setRoles(roles ?? []);
        } catch (error) {
            console.error("Failed to load user roles:", error);
            context.auth.roles?.setRoles([]);
        }
    },
});
